import { CONNECTIONS } from "@/app/features/diagrams/functions/heroSystem/constants";
import { heroTransition } from "@/app/features/diagrams/functions/heroSystem/motion";

export type NetworkConnectionsProps = {
  activeNode: string;
  selectedNode: string;
  isPathActiveForNode: (from: string, to: string, nodeId: string) => boolean;
  enablePacketFlow: boolean;
};

export function NetworkConnections({
  activeNode,
  selectedNode,
  isPathActiveForNode,
  enablePacketFlow,
}: NetworkConnectionsProps) {
  return (
    <g className="connections-layer">
      {CONNECTIONS.map((conn, idx) => {
        const isCore = conn.type === "core";
        const isOuter = !isCore;

        // Responsive: hide outer connections on mobile/tablet
        const connClass = isOuter ? "hidden md:block" : "";

        // Check active states
        const isHoveredPath =
          isPathActiveForNode(conn.from, conn.to, activeNode) &&
          activeNode !== selectedNode;
        const isSelectedPath = isPathActiveForNode(
          conn.from,
          conn.to,
          selectedNode,
        );
        const active = isHoveredPath || isSelectedPath;

        // Selected paths are brand signals, hovered paths are secondary slate
        const pathColor = isHoveredPath
          ? "var(--ga-moss)"
          : isSelectedPath
            ? "var(--ga-signal)"
            : "var(--ga-line)";

        const strokeWidth = isCore
          ? active
            ? 1.3
            : 1.0
          : active
            ? 0.95
            : 0.75;

        const opacity = active
          ? isHoveredPath
            ? 0.75
            : 0.9
          : isCore
            ? 0.45
            : 0.28;

        const motion = heroTransition(active);

        return (
          <g key={`conn-${idx}`} className={connClass}>
            {/* Active glow path (always mounted, transitions opacity and stroke-width) */}
            <path
              d={conn.d}
              stroke="var(--ga-signal)"
              strokeWidth={isSelectedPath ? (isCore ? 2.6 : 2.0) : 0}
              opacity={isSelectedPath ? 0.45 : 0}
              filter="url(#glow)"
              className={motion}
            />

            {/* Core highlight path (always mounted, transitions stroke, width, and opacity) */}
            <path
              d={conn.d}
              stroke={pathColor}
              strokeWidth={strokeWidth + 0.3}
              opacity={opacity}
              className={motion}
            />

            {/* Single packet flow overlay (representing a packet transfer) */}
            {enablePacketFlow && active && (
              <path
                d={conn.d}
                stroke={pathColor}
                strokeWidth={isCore ? strokeWidth + 0.5 : strokeWidth + 0.3}
                opacity={0.85}
                className="ga-packet"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}
